import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthLayout } from '@/components/AuthLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Shell } from '@/components/Shell';
import { useOnboarding } from '@/features/get-started/useOnboarding';
import { NotFound } from '@/screens';

// Route-level code splitting — each screen is its own chunk.
const Login = lazy(() => import('@/features/auth/Login').then((m) => ({ default: m.Login })));
const Catalog = lazy(() => import('@/features/catalog/Catalog').then((m) => ({ default: m.Catalog })));
const MyGreffons = lazy(() => import('@/features/greffons/MyGreffons').then((m) => ({ default: m.MyGreffons })));
const CreateCustomApp = lazy(() =>
  import('@/features/custom/CreateCustomApp').then((m) => ({ default: m.CreateCustomApp })),
);
const CustomAppPage = lazy(() =>
  import('@/features/custom/CustomAppPage').then((m) => ({ default: m.CustomAppPage })),
);
const GreffonDetail = lazy(() =>
  import('@/features/greffons/GreffonDetail').then((m) => ({ default: m.GreffonDetail })),
);
const Greffers = lazy(() => import('@/features/greffers/Greffers').then((m) => ({ default: m.Greffers })));
const GrefferDetail = lazy(() =>
  import('@/features/greffers/GrefferDetail').then((m) => ({ default: m.GrefferDetail })),
);
const Integrations = lazy(() => import('@/features/integrations/Integrations').then((m) => ({ default: m.Integrations })));
const Account = lazy(() => import('@/features/account/Account').then((m) => ({ default: m.Account })));
const Kit = lazy(() => import('@/screens/Kit').then((m) => ({ default: m.Kit })));
const GetStarted = lazy(() => import('@/features/get-started/GetStarted').then((m) => ({ default: m.GetStarted })));

function PageSpinner() {
  return (
    <div className="grid min-h-screen place-items-center bg-page">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-line-2 border-t-brand" />
    </div>
  );
}

/**
 * First-run landing guard. Routes to Get-started until the account has a running
 * instance (firstRun); otherwise the Catalog is home. On error or while settling
 * we send to Get-started (it renders the retry/loading state) rather than bounce
 * a real first-run user to the Catalog with a disabled Graft button.
 */
function DashboardIndex() {
  const { ready, isError, firstRun } = useOnboarding();
  // While the queries settle, render a skeleton (not bare null) so the content
  // pane doesn't flash blank before the redirect resolves (HLD §2).
  if (!ready && !isError) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3.5 rounded-lg border border-line bg-surface p-4 shadow-flat">
            <div className="h-7 w-7 animate-pulse rounded-full bg-surface-2" />
            <div className="flex-1">
              <div className="h-4 w-44 animate-pulse rounded bg-surface-2" />
              <div className="mt-2 h-3 w-60 animate-pulse rounded bg-surface-2" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return <Navigate to={firstRun || isError ? 'get-started' : 'store'} replace />;
}

/** Route table — paths preserved exactly as today's app (HLD §4). */
export default function App() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          {/* Passwordless + invite-only: no public signup. /register → /login. */}
          <Route path="/register" element={<Navigate to="/login" replace />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<Shell />}>
            <Route index element={<DashboardIndex />} />
            <Route path="get-started" element={<GetStarted />} />
            <Route path="store" element={<Catalog />} />
            <Route path="my-greffons" element={<MyGreffons />} />
            <Route path="custom-app" element={<CreateCustomApp />} />
            <Route path="custom-app/:id" element={<CustomAppPage />} />
            <Route path="my-greffons/:id" element={<GreffonDetail />} />
            <Route path="greffers" element={<Greffers />} />
            <Route path="greffers/:id" element={<GrefferDetail />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="account" element={<Account />} />
            <Route path="kit" element={<Kit />} />
          </Route>
        </Route>

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
}
